import { CommonModule } from '@angular/common';
import { Component, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeedbackService } from '../../services/feedback.service';
import { MessageService } from '../../services/message.service';

@Component({
  selector: 'slt-feedback-fab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="feedback-fab-container">
      <button
        mat-fab
        color="primary"
        aria-label="Send Feedback"
        matTooltip="Send Feedback"
        matTooltipPosition="left"
        (click)="openFeedbackModal()"
      >
        <mat-icon>chat_bubble_outline</mat-icon>
      </button>
    </div>

    <!-- Feedback Form Template to pass into MessageService -->
    <ng-template #feedbackTemplate>
      <div class="feedback-form-container">
        <p class="dialog-intro">
          Found a bug or have an idea for improvement? Let us know! Your feedback helps us make
          Suite Life Tools better.
        </p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Your Feedback / Bug Report</mat-label>
          <textarea
            matInput
            [(ngModel)]="message"
            rows="5"
            placeholder="Describe what you were doing or what could be improved..."
            [disabled]="isSubmitting()"
            cdkFocusInitial
          ></textarea>
        </mat-form-field>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .feedback-fab-container {
        position: fixed;
        bottom: 76px;
        right: 24px;
        z-index: 999;
      }
      .feedback-form-container {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        width: 100%;
        min-width: 320px;
      }
      .dialog-intro {
        font-size: 0.88rem;
        color: #546e7a;
        margin: 0 0 0.5rem 0;
        line-height: 1.4;
      }
      .full-width {
        width: 100%;
      }
    `,
  ],
})
export class FeedbackFabComponent {
  private messageService = inject(MessageService);
  private feedbackService = inject(FeedbackService);
  private snackBar = inject(MatSnackBar);

  @ViewChild('feedbackTemplate') feedbackTemplate!: TemplateRef<any>;

  message = '';
  isSubmitting = signal<boolean>(false);

  async openFeedbackModal(): Promise<void> {
    this.message = ''; // Reset input on open

    const result = await this.messageService.showAlert('Send Feedback', this.feedbackTemplate, [
      { label: 'Cancel', value: 'cancel', closeOnClick: true },
      {
        label: 'Submit Feedback',
        value: 'submit',
        isPrimary: true,
        closeOnClick: true,
        isFocused: true,
        isEnabled: () => this.message.trim().length > 0 && !this.isSubmitting(),
      },
    ]);

    if (result === 'submit') {
      await this.submitFeedback();
    }
  }

  private async submitFeedback(): Promise<void> {
    if (!this.message.trim() || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    const path = window.location.pathname;
    const module = path.includes('budget') ? 'budgeting' : 'general';

    this.feedbackService
      .submitFeedback({
        message: this.message,
        module: module,
        route_path: path,
      })
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.snackBar.open('Thank you! Your feedback has been sent.', 'Dismiss', {
            duration: 4000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        },
        error: (err) => {
          console.error('Failed to submit feedback:', err);
          this.isSubmitting.set(false);
          this.snackBar.open('Failed to send feedback. Please try again later.', 'Close', {
            duration: 4000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        },
      });
  }
}
